import React, { useEffect, useRef, useState } from 'react';
import { History, Loader2, Lock } from 'lucide-react';
import { losApi } from './losApi';
import { formatDateTime } from './kit/time';
import { errorMessage } from './errors';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PickSelect from './shared/PickSelect';

// Ticket 193 D1: the workspace audit trail, shown to owners and admins. The server
// is tenant-scoped and sends only who / what / when / which record, never an ip,
// user agent or the contents of a change.
const PAGE = 100;
const EMPTY = { who: '', action_prefix: '', category: '', record_id: '', from: '', to: '' };

const CATEGORIES = [
  { value: '', label: 'Changes, sign-ins and protected access' },
  { value: 'write', label: 'Changes' },
  { value: 'auth', label: 'Sign-ins' },
  { value: 'webhook', label: 'Integrations' },
  { value: 'read', label: 'Page views and reads' },
];

// A date picked in the filter is the viewer's local day: start of it, end of it.
const startOfDay = (d) => new Date(`${d}T00:00:00`).toISOString();
const endOfDay = (d) => new Date(`${d}T23:59:59.999`).toISOString();

const toParams = (f, extra = {}) => {
  const p = { limit: PAGE, ...extra };
  if (f.who.trim()) p.who = f.who.trim();
  if (f.action_prefix.trim()) p.action_prefix = f.action_prefix.trim();
  if (f.category) p.category = f.category;
  if (f.record_id.trim()) p.record_id = f.record_id.trim();
  if (f.from) p.from_ts = startOfDay(f.from);
  if (f.to) p.to_ts = endOfDay(f.to);
  return p;
};

export default function AuditLogTab() {
  const [form, setForm] = useState(EMPTY);
  const [applied, setApplied] = useState(EMPTY);
  const [state, setState] = useState({ loading: true, error: null, events: [], nextBefore: null });
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState('');
  const request = useRef(0);

  useEffect(() => {
    const mine = ++request.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    losApi.workspaceAudit(toParams(applied))
      .then((d) => {
        if (request.current !== mine) return;
        setState({ loading: false, error: null, events: d.events || [], nextBefore: d.next_before || null });
      })
      .catch((e) => {
        if (request.current !== mine) return;
        setState({ loading: false, error: errorMessage(e), events: [], nextBefore: null });
      });
    return () => { request.current += 1; };
  }, [applied]);

  const loadOlder = async () => {
    const mine = request.current;
    setLoadingOlder(true);
    setOlderError('');
    try {
      const d = await losApi.workspaceAudit(toParams(applied, { before: state.nextBefore }));
      if (request.current !== mine) return;
      setState((s) => ({ ...s, events: [...s.events, ...(d.events || [])], nextBefore: d.next_before || null }));
    } catch (e) {
      if (request.current === mine) setOlderError(errorMessage(e));
    } finally {
      setLoadingOlder(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const apply = (e) => { e.preventDefault(); setApplied(form); };
  const clear = () => { setForm(EMPTY); setApplied(EMPTY); };
  const { loading, error, events, nextBefore } = state;

  return (
    <div className="space-y-4" data-testid="audit-log-tab">
      <form onSubmit={apply} className="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="audit-filters">
        <div><Label htmlFor="audit-who">Who</Label><Input id="audit-who" value={form.who} onChange={set('who')} placeholder="Email or user id" className="mt-1" data-testid="audit-who" /></div>
        <div>
          <PickSelect label="What" labelClassName="text-sm font-medium leading-none" value={form.category} onChange={(v) => setForm((f) => ({ ...f, category: v }))} options={CATEGORIES} testId="audit-category" />
        </div>
        <div><Label htmlFor="audit-action">Event starts with</Label><Input id="audit-action" value={form.action_prefix} onChange={set('action_prefix')} placeholder="ssn., document., loan." className="mt-1" data-testid="audit-action" /></div>
        <div><Label htmlFor="audit-record">Loan or contact id</Label><Input id="audit-record" value={form.record_id} onChange={set('record_id')} placeholder="Paste an id from the URL" className="mt-1" data-testid="audit-record" /></div>
        <div><Label htmlFor="audit-from">From</Label><Input id="audit-from" type="date" value={form.from} onChange={set('from')} className="mt-1" data-testid="audit-from" /></div>
        <div><Label htmlFor="audit-to">To</Label><Input id="audit-to" type="date" value={form.to} onChange={set('to')} className="mt-1" data-testid="audit-to" /></div>
        <div className="md:col-span-3 flex gap-2">
          <button type="submit" className="text-sm font-semibold px-4 py-2 rounded-lg bg-brand-navy text-white" data-testid="audit-apply">Apply filters</button>
          <button type="button" onClick={clear} className="text-sm font-semibold px-4 py-2 rounded-lg border border-slate-200" data-testid="audit-clear">Clear</button>
        </div>
      </form>

      {loading ? (
        <div className="p-8 flex items-center justify-center text-slate-400" data-testid="audit-loading"><Loader2 className="animate-spin mr-2" size={18} /> Loading audit log...</div>
      ) : error ? (
        <div className="bg-red-50 border border-red-100 rounded-2xl p-8 text-center text-red-600 text-sm" data-testid="audit-error">{error}</div>
      ) : events.length === 0 ? (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-16 text-center text-slate-400" data-testid="audit-empty">No events match.</div>
      ) : (
        <ul className="space-y-2" data-testid="audit-list">
          {events.map((e, i) => {
            const sensitive = e.category === 'read';
            const Icon = sensitive ? Lock : History;
            const res = e.resource || {};
            return (
              <li key={e.event_id || `${e.ts}-${i}`} data-testid={`audit-event-${e.action}`} className="flex gap-3 items-start bg-white border border-slate-100 rounded-xl p-3">
                <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center ${sensitive ? 'bg-amber-50 text-amber-600' : 'bg-slate-100 text-slate-500'}`}><Icon size={14} /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-900 font-mono">{e.action}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {formatDateTime(e.ts)} · {e.actor_email || e.actor || 'System'}
                    {res.id ? ` · ${res.type || 'record'} ${res.id}` : ''}
                    {res.loan_file_id ? ` · loan ${res.loan_file_id}` : ''}
                  </div>
                </div>
                {e.severity && e.severity !== 'info' && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700" data-testid="audit-severity">{e.severity}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {nextBefore && !loading && !error && (
        <div className="text-center">
          <button type="button" onClick={loadOlder} disabled={loadingOlder} data-testid="audit-load-older" className="text-xs px-3 py-1 rounded-full border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60">
            {loadingOlder ? 'Loading...' : 'Load older'}
          </button>
          {olderError && <p className="text-xs text-red-600 mt-1" role="alert">{olderError}</p>}
        </div>
      )}
    </div>
  );
}
